'use client'

import { Ref, useEffect, useId, useImperativeHandle, useState } from 'react'
import ReactSelect, { components, MenuPlacement, OptionProps } from 'react-select'

import type { SelectOptionType } from '../../form/components/fields/select-field'

type TSelectValue = SelectOptionType['value']

// Drops the hover handlers: react-select re-renders every option on mouse move otherwise.
const SelectOption = (props: OptionProps<SelectOptionType, boolean>) => {
    const { onMouseMove, onMouseOver, ...innerProps } = props.innerProps

    return (
        <components.Option {...props} innerProps={innerProps} className="react-select-option" />
    )
}

type TProps = {
    ref?: Ref<TSelectRef>
    placeholder?: string
    options?: readonly SelectOptionType[]
    loadOptions?: () => Promise<readonly SelectOptionType[]>
    /**
     * Single select: the picked value, `undefined` when cleared. `isMulti`: the array of picked values.
     * Method syntax (bivariant) on purpose, so handlers typed for the single-value case stay assignable;
     * `null` is listed only for handlers declared as `(value?: string | number | null)`.
     */
    onChange(value?: TSelectValue | TSelectValue[] | null): void | Promise<void>
    isClearable?: boolean
    defaultValue?: TSelectValue
    menuPlacement?: MenuPlacement
    isMulti?: boolean
}

export type TSelectRef = {
    clear: () => void
    hasValue: () => boolean
    setValue: (value: string | number | string[] | number[]) => void
}

const Select = ({
    ref,
    placeholder,
    options: inputOptions,
    loadOptions,
    onChange,
    isClearable,
    defaultValue,
    menuPlacement,
    isMulti,
}: TProps) => {
    const [selectedValue, setSelectedValue] = useState<TSelectValue | TSelectValue[] | null>(defaultValue ?? null)
    const [loadedOptions, setLoadedOptions] = useState<readonly SelectOptionType[]>()
    const options = loadedOptions ?? inputOptions
    const instanceId = useId()

    useImperativeHandle(
        ref,
        () => ({
            clear: () => setSelectedValue(null),
            hasValue: () => selectedValue !== null,
            setValue: (value: string | number | string[] | number[]) => setSelectedValue(value),
        }),
        [selectedValue]
    )

    useEffect(() => {
        if (!loadOptions)
            return

        // a response of a superseded `loadOptions` (or one arriving after unmount) is dropped
        let ignore = false
        loadOptions()
            .then(data => {
                if (!ignore)
                    setLoadedOptions(data)
            })
            .catch(e => {
                console.error(e)
                if (!ignore)
                    setLoadedOptions([])
            })

        return () => {
            ignore = true
        }
    }, [loadOptions])

    // `null` (not `undefined`) keeps react-select controlled, so clearing really clears the display.
    const displayedValue = Array.isArray(selectedValue)
        ? (options ?? []).filter(opt => selectedValue.some(value => value == opt.value))
        : (options?.find(opt => opt.value == selectedValue) ?? null)

    return (
        <ReactSelect<SelectOptionType, boolean>
            className="react-select-primitive"
            instanceId={instanceId}
            value={displayedValue}
            components={{ Option: SelectOption }}
            options={options}
            isClearable={isClearable}
            isLoading={options === undefined}
            isMulti={isMulti}
            placeholder={placeholder}
            menuPlacement={menuPlacement}
            onChange={newValue => {
                let values: TSelectValue | TSelectValue[] | undefined
                if (Array.isArray(newValue))
                    values = newValue.map(val => val.value)
                else
                    values = (newValue as SelectOptionType | null)?.value

                setSelectedValue(values ?? null)
                onChange(values)
            }}
            styles={{
                menuPortal: base => ({
                    ...base,
                    zIndex: 9999,
                    pointerEvents: 'all',
                }),
                control: (base, state) => ({
                    ...base,
                    backgroundColor: 'var(--color-surface)',
                    borderColor: state.isFocused ? 'var(--accent-8)' : 'var(--gray-7)',
                    boxShadow: state.isFocused ? '0 0 0 1px var(--accent-8)' : 'none',
                    ':hover': {
                        borderColor: 'var(--accent-8)',
                    },
                }),
                placeholder: base => ({
                    ...base,
                    color: 'var(--gray-10)',
                }),
                singleValue: base => ({
                    ...base,
                    color: 'var(--gray-12)',
                }),
                input: base => ({
                    ...base,
                    color: 'var(--gray-12)',
                }),
                multiValue: base => ({
                    ...base,
                    backgroundColor: 'var(--gray-4)',
                }),
                multiValueLabel: base => ({
                    ...base,
                    color: 'var(--gray-12)',
                }),
                indicatorSeparator: base => ({
                    ...base,
                    backgroundColor: 'var(--gray-7)',
                }),
                menu: base => ({
                    ...base,
                    backgroundColor: 'var(--gray-1)',
                    border: '1px solid var(--gray-3)',
                    boxShadow: 'var(--shadow-2)',
                }),
                option: (base, state) => ({
                    ...base,
                    color: 'var(--gray-11)',
                    backgroundColor: state.isSelected
                        ? 'var(--blue-a4)'
                        : state.isFocused
                            ? 'var(--blue-a2)'
                            : 'var(--color-panel-solid)',
                    ':hover': {
                        backgroundColor: 'var(--blue-a2)',
                        cursor: 'pointer',
                    },
                }),
            }}
            autoFocus={false}
            menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
            menuPosition="fixed"
        />
    )
}

export default Select
